import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { customersAPI, showFlashMessage, showErrorModal } from '../services/api';
import { toast } from 'sonner';
import { formatPhoneNumber } from '../utils/formatters';

const CreateCustomer = () => {
  const navigate = useNavigate();

  // Customer Information
  const [type, setType] = useState('');
  const [customerNumber, setCustomerNumber] = useState('');
  const [company, setCompany] = useState('');
  const [primaryLastName, setPrimaryLastName] = useState('');
  const [primaryFirstName, setPrimaryFirstName] = useState('');
  const [primaryPhone, setPrimaryPhone] = useState('');
  const [alternateLastName, setAlternateLastName] = useState('');
  const [alternateFirstName, setAlternateFirstName] = useState('');
  const [alternatePhone, setAlternatePhone] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = React.useRef(false);
  const [attempted, setAttempted] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState({ open: false, matches: [] });

  // Address fields
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [billTo, setBillTo] = useState(false);
  const [current, setCurrent] = useState(true);

  const handleSaveCustomer = async (force = false) => {
    // Re-entrancy guard: a rapid double-click should not fire two POSTs.
    if (savingRef.current) return;
    savingRef.current = true;
    setAttempted(true);
    if (!type || !primaryLastName.trim() || !primaryFirstName.trim() || !primaryPhone.trim() || !email.trim() || !streetAddress.trim() || !city.trim() || !state.trim() || !zip.trim()) {
      showErrorModal('Please fill in all required fields (marked in red)');
      savingRef.current = false;
      return;
    }

    // Check for duplicate last names unless force-creating. Done server-side
    // so it is accurate regardless of how many customers exist and immune to
    // the old race where the client list hadn't finished loading.
    if (!force) {
      let matches = [];
      try {
        const res = await customersAPI.checkDuplicate(primaryLastName.trim());
        matches = res.matches || [];
      } catch {}
      if (matches.length > 0) {
        setDuplicateModal({ open: true, matches });
        // Release the lock so "Create Anyway" can proceed once the user
        // confirms in the duplicate modal.
        savingRef.current = false;
        return;
      }
    }
    
    setSaving(true);
    
    try {
      const addresses = [];
      if (streetAddress || city || state || zip) {
        addresses.push({
          street_address: streetAddress,
          city: city,
          state: state,
          zip: zip,
          bill_to: billTo,
          current: current
        });
      }
      
      const customerData = {
        type: type,
        number: customerNumber,
        company: company,
        primary_last_name: primaryLastName,
        primary_first_name: primaryFirstName,
        primary_phone: primaryPhone,
        alternate_last_name: alternateLastName,
        alternate_first_name: alternateFirstName,
        alternate_phone: alternatePhone,
        email: email,
        addresses: addresses
      };
      
      await customersAPI.create(customerData);
      showFlashMessage('Customer saved successfully');
      navigate('/customers');
      
    } catch (error) {
      console.error('Error saving customer:', error);
      if (error.response?.status === 401) return;
      const detail = error.response?.data?.detail || error.message || 'An unknown error occurred while saving the customer';
      showErrorModal(detail);
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  return (
    <Layout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-lg text-[#5B8DB8] font-light">Customers</h2>
          <h1 className="text-3xl text-gray-700 font-light">Create Customer</h1>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded shadow p-6">
          <div className="grid grid-cols-2 gap-8">
            {/* Left Column - Customer Information */}
            <div>
              <h2 className="text-xl text-gray-700 mb-6">Customer Information</h2>
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-red-500 text-sm w-40 text-right">Type:</label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className={`border rounded px-3 py-2 flex-1 ${attempted && !type ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                    data-testid="customer-type-select"
                  >
                    <option value="">-- select --</option>
                    <option value="Residential">Residential</option>
                    <option value="Commercial">Commercial</option>
                  </select>
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm w-40 text-right">Customer Number:</label>
                  <input
                    type="text"
                    value=""
                    placeholder="Auto-assigned on save"
                    disabled
                    className="border border-gray-300 rounded px-3 py-2 flex-1 bg-gray-100 text-gray-500"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm w-40 text-right">Company:</label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 flex-1"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-red-500 text-sm w-40 text-right">Primary Last Name:</label>
                  <input
                    type="text"
                    value={primaryLastName}
                    onChange={(e) => setPrimaryLastName(e.target.value)}
                    className={`border rounded px-3 py-2 flex-1 ${(attempted && !primaryLastName.trim()) ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                    data-testid="primary-last-name-input"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-red-500 text-sm w-40 text-right">Primary First Name:</label>
                  <input
                    type="text"
                    value={primaryFirstName}
                    onChange={(e) => setPrimaryFirstName(e.target.value)}
                    className={`border rounded px-3 py-2 flex-1 ${attempted && !primaryFirstName.trim() ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                    data-testid="primary-first-name-input"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-red-500 text-sm w-40 text-right">Primary Phone:</label>
                  <input
                    type="text"
                    value={primaryPhone}
                    onChange={(e) => setPrimaryPhone(formatPhoneNumber(e.target.value))}
                    placeholder="215-555-1234"
                    className={`border rounded px-3 py-2 flex-1 ${attempted && !primaryPhone.trim() ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                    data-testid="primary-phone-input"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm w-40 text-right">Alternate Last Name:</label>
                  <input
                    type="text"
                    value={alternateLastName}
                    onChange={(e) => setAlternateLastName(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 flex-1"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm w-40 text-right">Alternate First Name:</label>
                  <input
                    type="text"
                    value={alternateFirstName}
                    onChange={(e) => setAlternateFirstName(e.target.value)}
                    className="border border-gray-300 rounded px-3 py-2 flex-1"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm w-40 text-right">Alternate Phone:</label>
                  <input
                    type="text"
                    value={alternatePhone}
                    onChange={(e) => setAlternatePhone(formatPhoneNumber(e.target.value))}
                    placeholder="215-555-1234"
                    className="border border-gray-300 rounded px-3 py-2 flex-1"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-red-500 text-sm w-40 text-right">Email:</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`border rounded px-3 py-2 flex-1 ${attempted && !email.trim() ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="mt-8 flex gap-4">
                <button
                  onClick={() => navigate('/customers')}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded transition-colors"
                  data-testid="back-btn-create-customer"
                >
                  Back
                </button>
                <button
                  onClick={() => handleSaveCustomer(false)}
                  disabled={saving}
                  className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-6 py-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-customer-btn"
                >
                  {saving ? 'Saving...' : 'Save Customer'}
                </button>
              </div>
            </div>

            {/* Right Column - Addresses and Projects */}
            <div>
              {/* Customer Addresses */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl text-gray-700">Customer Addresses</h2>
                  <button 
                    disabled
                    className="bg-gray-400 text-white px-4 py-2 rounded transition-colors flex items-center gap-2 text-sm cursor-not-allowed opacity-50"
                    title="Save customer first to add more addresses"
                  >
                    <span>+</span> New Address
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 text-red-500 font-normal">Street Address</th>
                        <th className="text-left p-2 text-red-500 font-normal">City</th>
                        <th className="text-left p-2 text-red-500 font-normal">State</th>
                        <th className="text-left p-2 text-red-500 font-normal">Zip</th>
                        <th className="text-left p-2 text-gray-600 font-normal">Bill to</th>
                        <th className="text-left p-2 text-gray-600 font-normal">Current</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="p-2">
                          <input
                            type="text"
                            value={streetAddress}
                            onChange={(e) => setStreetAddress(e.target.value)}
                            className={`border rounded px-2 py-1 w-full ${attempted && !streetAddress.trim() ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="text"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            className={`border rounded px-2 py-1 w-full ${attempted && !city.trim() ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="text"
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                            className={`border rounded px-2 py-1 w-full ${attempted && !state.trim() ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="text"
                            value={zip}
                            onChange={(e) => setZip(e.target.value)}
                            className={`border rounded px-2 py-1 w-16 ${attempted && !zip.trim() ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                          />
                        </td>
                        <td className="p-2 text-center">
                          <input
                            type="checkbox"
                            checked={billTo}
                            onChange={(e) => setBillTo(e.target.checked)}
                            className="w-4 h-4"
                          />
                        </td>
                        <td className="p-2 text-center">
                          <input
                            type="checkbox"
                            checked={current}
                            onChange={(e) => setCurrent(e.target.checked)}
                            className="w-4 h-4 accent-[#4ECFDB]"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Customer Projects */}
              <div>
                <h2 className="text-xl text-gray-700 mb-4">Customer Projects</h2>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2">
                        <th className="text-left p-2 text-gray-600 font-normal">ID</th>
                        <th className="text-left p-2 text-gray-600 font-normal">Street Address</th>
                        <th className="text-left p-2 text-gray-600 font-normal">Location</th>
                        <th className="text-left p-2 text-gray-600 font-normal">Type</th>
                        <th className="text-left p-2 text-gray-600 font-normal">FNOL Date</th>
                        <th className="text-left p-2 text-gray-600 font-normal">Start Date</th>
                        <th className="text-left p-2 text-gray-600 font-normal">Completion Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Empty - no projects for new customer */}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Duplicate Customer Modal */}
      {duplicateModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="duplicate-modal-overlay">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 relative max-h-[90vh] flex flex-col" data-testid="duplicate-modal">
            <button
              onClick={() => setDuplicateModal({ open: false, matches: [] })}
              className="absolute top-3 right-4 text-gray-400 hover:text-gray-600 text-xl"
            >
              &times;
            </button>
            <div className="p-6 flex-1 min-h-0 flex flex-col">
              <h2 className="text-2xl text-[#C0392B] font-light italic mb-4">Duplicate Customers Found!</h2>
              <p className="text-gray-600 text-sm mb-6">
                The following customers were found with the information provided. Please review and select "Create Customer" to create the customer. Otherwise use one of the customers below.
              </p>
              <div className="overflow-auto mb-6 flex-1 min-h-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 sticky top-0 bg-white z-10">
                      <th className="text-left p-2 text-gray-500 font-normal">Customer #</th>
                      <th className="text-left p-2 text-gray-500 font-normal">Company</th>
                      <th className="text-left p-2 text-gray-500 font-normal">Customer Name</th>
                      <th className="text-left p-2 text-gray-500 font-normal">Primary Phone</th>
                      <th className="text-left p-2 text-gray-500 font-normal">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {duplicateModal.matches.map((c, i) => (
                      <tr
                        key={i}
                        className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/customers/${c.customer_id}`)}
                        data-testid={`duplicate-row-${i}`}
                      >
                        <td className="p-2 text-gray-700">{c.customer_id}</td>
                        <td className="p-2 text-gray-700">{c.company || ''}</td>
                        <td className="p-2 text-gray-700">{c.primary_last_name || ''}, {c.primary_first_name || ''}</td>
                        <td className="p-2 text-gray-700">{c.primary_phone || ''}</td>
                        <td className="p-2 text-gray-700">{c.email || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => setDuplicateModal({ open: false, matches: [] })}
                  className="border border-gray-300 text-gray-600 px-6 py-2 rounded hover:bg-gray-100 transition-colors"
                  data-testid="duplicate-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setDuplicateModal({ open: false, matches: [] }); handleSaveCustomer(true); }}
                  className="bg-[#C0392B] hover:bg-[#A93226] text-white px-6 py-2 rounded transition-colors"
                  data-testid="duplicate-create-btn"
                >
                  Create Customer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CreateCustomer;
